import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield, Zap, Star, Award, Download, Loader2, ExternalLink, Sparkles,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BADGE_VISUALS = {
  escrow: { Icon: Shield, color: "text-emerald-300", bg: "bg-emerald-500/15", border: "border-emerald-500/40" },
  speed: { Icon: Zap, color: "text-amber-300", bg: "bg-amber-500/15", border: "border-amber-500/40" },
  elite: { Icon: Star, color: "text-violet-300", bg: "bg-violet-500/15", border: "border-violet-500/40" },
};

const RANK_GRADIENT = {
  bronze: "from-[#B8742C] via-[#8B5A1E] to-[#5C3A12]",
  silver: "from-[#E2E8F0] via-[#94A3B8] to-[#475569]",
  gold: "from-[#FDE68A] via-[#D4AF37] to-[#92400E]",
  platinum: "from-[#7DD3FC] via-[#0EA5E9] to-[#1E40AF]",
};

const formatFCFA = (n) => Number(n || 0).toLocaleString("fr-FR");

export default function ArtisanTrustPass({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [specs, setSpecs] = useState([]);

  useEffect(() => {
    axios.get(`${API}/artisans/specialties`).then((r) => setSpecs(r.data || [])).catch(() => {});
  }, []);

  const fetch = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/artisans/${user.id}/trust-pass`, {
        params: { public_base: BACKEND_URL },
      });
      setData(r.data);
    } catch (err) {
      console.error("TrustPass fetch:", err);
      if (err.response?.status === 404) {
        // pas de profil artisan encore — silencieux
      } else {
        toast.error("Erreur de chargement du Trust Pass");
      }
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const downloadCertificate = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/artisans/${user.id}/certificate.pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificat-arteknos-${(data?.full_name || "artisan").replace(/\s+/g, "-")}-${data?.year}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Certificat téléchargé");
    } catch {
      toast.error("Impossible de générer le certificat");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#1E293B] border border-white/5 rounded-[2.5rem] p-12 flex items-center justify-center" data-testid="trust-pass-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-[#1E293B] border border-white/5 rounded-[2.5rem] p-8 text-center" data-testid="trust-pass-empty">
        <Award className="w-10 h-10 text-slate-600 mx-auto mb-3" />
        <p className="text-sm text-slate-300 font-bold">Complétez votre profil artisan</p>
        <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-1">pour activer votre Trust Pass</p>
      </div>
    );
  }

  const rankGradient = RANK_GRADIENT[data.trust.current_rank] || RANK_GRADIENT.bronze;
  const initials = (data.full_name || "A").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  const nextRankLabel = data.trust.next_rank_label;

  return (
    <div className="space-y-4" data-testid="artisan-trust-pass">
      {/* ============ APPLE WALLET STYLE CARD ============ */}
      <div
        className={`relative overflow-hidden rounded-[2.5rem] p-6 md:p-7 bg-gradient-to-br ${rankGradient} shadow-2xl`}
        data-testid="trust-pass-card"
      >
        {/* Pattern overlay */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 right-0 w-72 h-72 bg-white rounded-full -translate-y-1/2 translate-x-1/3 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-black rounded-full translate-y-1/2 -translate-x-1/4 blur-2xl" />
        </div>

        {/* Top row : avatar + name vs QR */}
        <div className="relative z-10 flex justify-between items-start gap-4 mb-6">
          <div className="flex items-center gap-3 min-w-0">
            {data.avatar_url ? (
              <img
                src={data.avatar_url}
                alt={data.full_name}
                className="w-14 h-14 rounded-2xl object-cover ring-2 ring-white/30 flex-shrink-0"
              />
            ) : (
              <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center text-white font-black text-lg flex-shrink-0 ring-2 ring-white/20">
                {initials}
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[10px] text-white/70 font-black uppercase tracking-[0.25em]">ARTEKNOS Trust Pass</p>
              <h2 className="text-lg md:text-xl font-black text-white tracking-tight truncate" data-testid="trust-name">
                {data.full_name}
              </h2>
              <p className="text-[10px] text-white/80 uppercase tracking-widest mt-0.5">
                {(data.specialties || []).slice(0, 2).map((k) => specs.find((x) => x.key === k)?.label || k).join(" · ") || "Artisan certifié"}
              </p>
            </div>
          </div>

          {/* QR Code */}
          {data.qr_data_url && (
            <button
              onClick={() => data.verify_url && window.open(data.verify_url, "_blank")}
              className="bg-white p-1.5 rounded-2xl flex-shrink-0 hover:scale-105 active:scale-95 transition-transform"
              title="Aperçu de la page de vérification publique"
              data-testid="trust-pass-qr"
            >
              <img src={data.qr_data_url} alt="QR de vérification" className="w-16 h-16 md:w-20 md:h-20" />
            </button>
          )}
        </div>

        {/* Rank + score */}
        <div className="relative z-10 bg-black/25 backdrop-blur-sm rounded-2xl p-4 mb-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-[9px] text-white/60 font-black uppercase tracking-widest">Rang actuel</p>
            <p className="text-2xl font-black italic text-white uppercase tracking-tight" data-testid="trust-rank">
              {data.trust.current_rank_label}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[9px] text-white/60 font-black uppercase tracking-widest">Score</p>
            <p className="text-2xl font-black text-white tracking-tighter">{data.trust.score_pct}<span className="text-sm">%</span></p>
          </div>
        </div>

        {/* Stats inline */}
        <div className="relative z-10 grid grid-cols-3 gap-2 mb-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
            <p className="text-[9px] text-white/60 font-black uppercase tracking-widest">Note</p>
            <p className="text-white font-black text-sm">{data.rating.toFixed(2)}/5</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
            <p className="text-[9px] text-white/60 font-black uppercase tracking-widest">Projets</p>
            <p className="text-white font-black text-sm">{data.completed_projects}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
            <p className="text-[9px] text-white/60 font-black uppercase tracking-widest">{data.year}</p>
            <p className="text-white font-black text-sm">{formatFCFA(data.stats_year?.total_earned)} F</p>
          </div>
        </div>

        {/* NSIC seal */}
        <div className="relative z-10 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-3 flex items-center gap-3" data-testid="nsic-seal">
          <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center text-[#0F172A] flex-shrink-0">
            <Shield className="w-4 h-4" fill="currentColor" />
          </div>
          <div>
            <p className="text-[10px] text-white font-black uppercase tracking-[0.3em]">NSIC · Assuré & Certifié</p>
            <p className="text-[9px] text-white/70 uppercase tracking-widest">Couverture plateforme ARTEKNOS</p>
          </div>
        </div>

        {/* Progress to next rank */}
        {nextRankLabel && (
          <div className="relative z-10 mt-4">
            <div className="flex justify-between text-[9px] text-white/70 uppercase tracking-widest font-black mb-1">
              <span>Progression</span>
              <span>Vers {nextRankLabel}</span>
            </div>
            <div className="h-2 bg-black/30 rounded-full overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-all duration-700"
                style={{ width: `${Math.min(100, (data.trust.score / (data.trust.next_rank_min_score || 1)) * 100)}%` }}
                data-testid="trust-progress-bar"
              />
            </div>
          </div>
        )}
      </div>

      {/* ============ BADGES ============ */}
      {data.trust.badges?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="trust-badges">
          {data.trust.badges.map((b) => {
            const visual = BADGE_VISUALS[b.key] || BADGE_VISUALS.escrow;
            const Icon = visual.Icon;
            return (
              <div
                key={b.key}
                className={`${visual.bg} ${visual.border} border rounded-2xl p-4 flex items-start gap-3`}
                data-testid={`badge-${b.key}`}
              >
                <div className={`w-9 h-9 rounded-xl bg-black/30 flex items-center justify-center ${visual.color} flex-shrink-0`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className={`text-xs font-black uppercase tracking-widest ${visual.color}`}>{b.label}</p>
                  <p className="text-[10px] text-slate-300/80 mt-0.5 leading-snug">{b.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data.trust.badges?.length === 0 && (
        <div className="bg-[#1E293B] border border-dashed border-white/10 rounded-2xl p-5 flex items-center gap-3" data-testid="trust-no-badges">
          <Sparkles className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
          <p className="text-[11px] text-slate-400 leading-relaxed">
            Complétez vos premiers projets via ARTEKNOS pour débloquer vos badges (Sécurité, Rapidité, Élite).
          </p>
        </div>
      )}

      {/* ============ ACTIONS ============ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <button
          onClick={downloadCertificate}
          disabled={downloading}
          className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:opacity-90 disabled:opacity-50 text-[#0F172A] py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-[#D4AF37]/20 flex items-center justify-center gap-2"
          data-testid="download-certificate-btn"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Télécharger mon certificat
        </button>
        {data.verify_url && (
          <a
            href={data.verify_url}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white/5 hover:bg-white/10 border border-white/10 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2"
            data-testid="open-public-verify-link"
          >
            <ExternalLink className="w-4 h-4" /> Voir ma page publique
          </a>
        )}
      </div>
    </div>
  );
}
